import { Facet, RangeSetBuilder } from "@codemirror/state";
import {
  Decoration,
  DecorationSet,
  EditorView,
  ViewPlugin,
  ViewUpdate,
} from "@codemirror/view";

export type CaretRange = {
  startLn: number;
  startCol: number;
  endLn: number;
  endCol: number;
};

export const matchedPatternsFacet = Facet.define<CaretRange[], CaretRange[]>({
  combine: (rangeArrays) => rangeArrays.flat(),
});
export const failedPatternsFacet = Facet.define<CaretRange[], CaretRange[]>({
  combine: (rangeArrays) => rangeArrays.flat(),
});
export const unusedPatternsFacet = Facet.define<CaretRange[], CaretRange[]>({
  combine: (rangeArrays) => rangeArrays.flat(),
});
export const breakpointLineFacet = Facet.define<number, number>({
  combine: (lineNumbers) => Math.min(...lineNumbers),
});

const lineRangePattern =
  /(?<startLn>\d+):(?<startCol>\d+)-(?<endLn>\d+):(?<endCol>\d+),?/g;
export function parse(lineRanges: string): CaretRange[] {
  const ranges: CaretRange[] = [];
  let matchResult = null;
  while ((matchResult = lineRangePattern.exec(lineRanges)) !== null) {
    if (matchResult.groups?.startLn === undefined) continue;
    const startLn = parseInt(matchResult.groups.startLn, 10);
    const startCol = parseInt(matchResult.groups.startCol, 10);
    const endLn = parseInt(matchResult.groups.endLn, 10);
    const endCol = parseInt(matchResult.groups.endCol, 10);
    ranges.push({ startLn, startCol, endLn, endCol });
  }
  return ranges;
}

const matchedPatternMark = Decoration.mark({
  attributes: { class: "cm-pattern cm-matchedPattern" },
});
const failedPatternMark = Decoration.mark({
  attributes: { class: "cm-pattern cm-failedPattern" },
});
const unusedPatternMark = Decoration.mark({
  attributes: { class: "cm-pattern cm-unusedPattern" },
});
const breakpointLineDeco = Decoration.line({
  attributes: { class: "cm-breakpointLine" },
});
const breakpointLineTextDeco = Decoration.mark({
  attributes: { class: "cm-breakpointLineText" },
});

export const viewPlugin = ViewPlugin.fromClass(
  class {
    decorations: DecorationSet;

    constructor(view: EditorView) {
      const matchedPatterns = view.state.facet(matchedPatternsFacet);
      const failedPatterns = view.state.facet(failedPatternsFacet);
      const unusedPatterns = view.state.facet(unusedPatternsFacet);
      const breakpointLine = view.state.facet(breakpointLineFacet);
      console.group("ViewPlugin.constructor");
      const marks: { start: number; end: number; mark: Decoration }[] = [];
      for (const { startLn, startCol, endLn, endCol } of matchedPatterns) {
        const start = view.state.doc.line(startLn).from + startCol - 1;
        const end = view.state.doc.line(endLn).from + endCol - 1;
        console.log(
          `Matched: ${startLn}:${startCol}-${endLn}:${endCol} to ${start}-${end}`
        );
        marks.push({ start, end, mark: matchedPatternMark });
      }
      for (const { startLn, startCol, endLn, endCol } of failedPatterns) {
        const start = view.state.doc.line(startLn).from + startCol - 1;
        const end = view.state.doc.line(endLn).from + endCol - 1;
        console.log(
          `Failed: ${startLn}:${startCol}-${endLn}:${endCol} to ${start}-${end}`
        );
        marks.push({ start, end, mark: failedPatternMark });
      }
      for (const { startLn, startCol, endLn, endCol } of unusedPatterns) {
        const start = view.state.doc.line(startLn).from + startCol - 1;
        const end = view.state.doc.line(endLn).from + endCol - 1;
        console.log(
          `Unused: ${startLn}:${startCol}-${endLn}:${endCol} to ${start}-${end}`
        );
        marks.push({ start, end, mark: unusedPatternMark });
      }
      console.groupEnd();
      // It is required to sort the marks by start position.
      marks.sort((a, b) => a.start - b.start);
      const builder = new RangeSetBuilder<Decoration>();
      for (const { start, end, mark } of marks) {
        builder.add(start, end, mark);
      }
      // Add breakpoint line
      if (
        Number.isInteger(breakpointLine) &&
        0 <= breakpointLine &&
        breakpointLine <= view.state.doc.lines
      ) {
        const line = view.state.doc.line(breakpointLine);
        builder.add(line.from, line.from, breakpointLineDeco);
        builder.add(line.from, line.to, breakpointLineTextDeco);
      }
      this.decorations = builder.finish();
    }

    // No need to update because our editors are read-only.
    update(update: ViewUpdate) {}
  },
  {
    decorations: (v) => v.decorations,
  }
);
